import {useFormik} from 'formik';
import * as Yup from 'yup';
import {useState} from 'react';
import {AxiosError} from 'axios';
import {useQueryClient, useMutation} from '@tanstack/react-query';
import {useNotifications} from '../../../shared';
import {projectApi, DepositFundsDTO} from '../../../entities';

interface UseDepositFundsFormProps {
  setOpen: (open: boolean) => void;
}

interface DepositFundsFormData {
  amount: string;
  reason: string;
}

export const useDepositFundsForm = ({setOpen}: UseDepositFundsFormProps) => {
  const queryClient = useQueryClient();
  const {createNotification} = useNotifications();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [formDataForConfirm, setFormDataForConfirm] = useState<DepositFundsDTO | null>(null);

  const depositFundsMutation = useMutation({
    mutationFn: projectApi.depositFunds,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['project']});
      queryClient.invalidateQueries({queryKey: ['balance-adjustments', 'infinite'], refetchType: 'all'});
      createNotification({
        title: 'Средства успешно внесены',
        name: 'depositFunds',
        theme: 'success',
      });
      setShowConfirmModal(false);
      setOpen(false);
    },
  });

  const onSubmit = async (values: DepositFundsFormData) => {
    const depositData: DepositFundsDTO = {
      amount: parseFloat(values.amount),
      reason: values.reason || '',
    };

    setFormDataForConfirm(depositData);
    setShowConfirmModal(true);
  };

  const handleConfirmDeposit = async () => {
    if (!formDataForConfirm) return;

    try {
      await depositFundsMutation.mutateAsync(formDataForConfirm);
    } catch (error: any) {
      console.error(error);
    }
  };

  const formik = useFormik<DepositFundsFormData>({
    initialValues: {
      amount: '',
      reason: '',
    },
    validationSchema: Yup.object({
      amount: Yup.number()
        .typeError('Должно быть числом')
        .required('Сумма обязательна')
        .positive('Сумма должна быть положительной'),
      reason: Yup.string().required('Комментарий обязателен'),
    }),
    onSubmit,
  });

  return {
    values: formik.values,
    errors: formik.errors,
    touched: formik.touched,
    handleChange: formik.handleChange,
    handleBlur: formik.handleBlur,
    setFieldValue: formik.setFieldValue,
    setFieldTouched: formik.setFieldTouched,
    resetForm: formik.resetForm,
    submitForm: formik.submitForm,
    isLoading: depositFundsMutation.isPending,
    submitError: depositFundsMutation.error
      ? (depositFundsMutation.error as AxiosError<ApiError>).response?.data
      : undefined,
    showConfirmModal,
    setShowConfirmModal,
    formDataForConfirm,
    handleConfirmDeposit,
  };
};
